import apiClient, { ApiError } from './client';
import { API_BASE_URL, API_ENDPOINTS, STORAGE_KEYS } from './config';
import type {
	CustomerDashboardParams,
	CustomerDashboardResponse,
	CreateCustomerRequest,
	CreateCustomerResponse,
	CustomerImportJob,
	UpdateCustomerRequest,
} from '../types/customer';

export const customersApi = {
	getDashboard: async (params: CustomerDashboardParams): Promise<CustomerDashboardResponse> => {
		const query = new URLSearchParams();
		query.append('locationId', String(params.locationId));
		if (params.search) query.append('search', params.search);
		if (params.sortBy) query.append('sortBy', params.sortBy);
		if (params.page !== undefined) query.append('page', String(params.page));
		if (params.size !== undefined) query.append('size', String(params.size));
		return apiClient.get<CustomerDashboardResponse>(
			`${API_ENDPOINTS.CUSTOMER.DASHBOARD}?${query.toString()}`
		);
	},

	create: async (data: CreateCustomerRequest): Promise<CreateCustomerResponse> => {
		return apiClient.post<CreateCustomerResponse>(API_ENDPOINTS.CUSTOMER.BASE, data);
	},

	update: async (id: number, data: UpdateCustomerRequest): Promise<CreateCustomerResponse> => {
		return apiClient.put<CreateCustomerResponse>(API_ENDPOINTS.CUSTOMER.DETAIL(id), data);
	},

	exportCsv: async (locationId: number): Promise<string> => {
		return apiClient.get<string>(`${API_ENDPOINTS.CUSTOMER.EXPORT}?locationId=${locationId}`);
	},

	// ─── CSV import ──────────────────────────────────────────────

	/** Header-only CSV template (returns the CSV text). */
	downloadImportTemplate: async (): Promise<string> => {
		return apiClient.get<string>(API_ENDPOINTS.CUSTOMER.IMPORT_TEMPLATE);
	},

	/**
	 * Upload a customer CSV for a location. Raw fetch because apiClient always sends
	 * JSON; the browser sets the multipart boundary itself.
	 */
	importCsv: async (locationId: number, file: File): Promise<CustomerImportJob> => {
		const formData = new FormData();
		formData.append('file', file);

		const token =
			localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) ||
			sessionStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);

		const response = await fetch(
			`${API_BASE_URL}${API_ENDPOINTS.CUSTOMER.IMPORT}?locationId=${locationId}`,
			{
				method: 'POST',
				headers: token ? { Authorization: `Bearer ${token}` } : {},
				body: formData,
			}
		);

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new ApiError(
				errorData?.message || 'Import failed',
				response.status,
				errorData?.fieldErrors
			);
		}

		return response.json();
	},

	getImportStatus: async (jobId: number): Promise<CustomerImportJob> => {
		return apiClient.get<CustomerImportJob>(API_ENDPOINTS.CUSTOMER.IMPORT_STATUS(jobId));
	},
};
